import {
    Box,
    Breadcrumbs,
    BreadcrumbsItem,
    DefaultButton,
    Divider,
    PrimaryButton,
    useSnackbar,
} from '@bb-ui/react-library';
import React, {
    FunctionComponent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { t } from 'i18next';
import axios from 'axios';
import { Fade, Tooltip } from '@material-ui/core';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { Warning } from '@bb-ui/icons/dist/medium';
import { Trans } from 'react-i18next';
import { useAuth0Context } from '../../../../auth/auth0';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { useUser } from '../../../../hooks/useUser';
import { getTenantId } from '../../../../utilities/utilities';
import { useApplicationTitle } from '../../../../hooks/useApplicationTitle';
import { useHelpLinks } from '../../../../hooks/useHelpLinks';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { LoadingIndicator } from '../../../LoadingIndicator/LoadingIndicator';
import { SkeletonTable } from '../../../Skeleton/SkeletonTable';
import { ResponsiveBox } from '../../../ResponsiveBox/ResponsiveBox';
import { Frame } from '../../../Frame/Frame';
import { UserInfoContainer } from './UserInfoContainer/UserInfoContainer';
import { UserRoles } from './UserRoles/UserRoles';
import { ActionAreaButtons } from '../../../ActionAreaButtons/ActionAreaButtons';
import { useStyles } from './EditUserPage.styles';
import { IHNodes } from './IHNodes/IHNodes';
import { INodeResponse, TreeNode } from './IHNodes/IHNodes.types';
import { EditUserError } from './EditUserError/EditUserError';
import { useLicenseManagementContext } from '../../../../contexts/LicenseManagementContext';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { UserDeleteDialog } from '../DeleteDialog/UserDeleteDialog';
import { isUserDeletable } from '../DeleteDialog/userIsDeletable';
import { UnsavedChangesDialog } from '../../../UnsavedChangesDialog/UnsavedChangesDialog';
import { UserIlluminateRoles } from './UserIlluminateRoles/UserIlluminateRoles';
import { useFeatureContext } from '../../../../contexts/FeatureContext';
import { FeatureName } from '../../../../featureFlags/Feature.types';
import {
    ADMIN_ROLE_NAME,
    AUTHOR_ROLE_NAME,
    DEVELOPER_ROLE_NAME,
    VIEWER_ROLE_NAME,
} from '../roles';
import { UserAssetsTransferDialog } from './UserAssetsTransferDialog/UserAssetsTransferDialog';
import { EmptyRolesDialog } from './EmptyRolesDialog/EmptyRolesDialog';

function arraysHaveSameElements(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, index) => val === sortedB[index]);
}

function nodesRecordsAreEqual(
    a: Record<string, string[]>,
    b: Record<string, string[]>,
): boolean {
    const normalize = (obj: Record<string, string[]>) =>
        JSON.stringify(
            Object.keys(obj)
                .sort()
                .reduce(
                    (acc, key) => {
                        acc[key] = [...(obj[key] || [])].sort();
                        return acc;
                    },
                    {} as Record<string, string[]>,
                ),
        );
    return normalize(a) === normalize(b);
}

// Stable empty array to avoid re-render loops when initialNodeIds is empty
const emptyNodeIds: string[] = [];

export const EditUserPage: FunctionComponent = () => {
    // TODO change user_name to real user_id

    const classes = useStyles();
    const history = useHistory();
    const { uas, api } = useAppConfigContext();
    const userIlluminateRolesEnabled =
        useFeatureContext()[
            'bbdata.roles.permissions.user.role.management' as FeatureName
        ];

    const {
        loading,
        users,
        error: usersApiError,
        replaceUserData,
    } = useLicenseManagementContext();
    const { enqueueSnackbar } = useSnackbar();

    const { user: auth0ContextUser, accessToken } = useAuth0Context();
    const tenantId = getTenantId(auth0ContextUser, uas);
    const sessionUser = useUser();

    useApplicationTitle(t('settings.editUser.title'));

    const getHelpLink = useHelpLinks();

    const { id: userId } = useParams() as {
        id: string;
    };

    const [selectedUser, setSelectedUser] = useState<User>();
    const [saveClicked, setSaveClicked] = useState<boolean>(false);
    const [emptyRolesDialogOpen, setEmptyRolesDialogOpen] =
        useState<boolean>(false);
    const [selectedNodes, setSelectedNodes] = useState<
        Record<string, string[]>
    >({});
    const [selectedQsGroups, setSelectedQsGroups] = useState<string[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [showDataError, setShowDataError] = useState<boolean>(false);

    const [isIHNodesLoading, setIsIHNodesLoading] = useState<boolean>(false);

    // Retrieve root-level nodes per learn tenant
    const [learnTenantNodes, setLearnTenantNodes] = useState<
        Record<string, TreeNode[]>
    >({});
    const [learnTenantNodesLoading, setLearnTenantNodesLoading] =
        useState<boolean>(true);
    const [learnTenantNodesError, setLearnTenantNodesError] = useState<
        string | undefined
    >(undefined);

    const [userToDelete, setUserToDelete] = useState<User | undefined>();
    const [userToAssetCheck, setUserToAssetCheck] = useState<
        User | undefined
    >();

    const [userWasRemoved, setUserWasRemoved] = useState<boolean>(false);
    const [transitionPostUserKill, setTransitionPostUserKill] =
        useState<boolean>(false);

    const [userIsDeletable, userUndeletabilityReason] = selectedUser
        ? isUserDeletable(
              selectedUser,
              sessionUser?.userId,
              t,
              userIlluminateRolesEnabled,
          )
        : [false, ''];

    const userApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: `${api!.userManagement}/v2/tenants/${tenantId}/users/${encodeURIComponent(selectedUser?.userId || '')}`,
            method: 'PATCH',
            useAuth: true,
            executeType: 'manual',
            data: {
                ...selectedUser,
                qsGroups: selectedQsGroups.map(gn => ({ name: gn })),
                roles: selectedRoles.map(rn => ({ name: rn })),
                dataNodes: Object.fromEntries(
                    Object.entries(selectedNodes).filter(
                        ([, nodes]) => nodes.length > 0,
                    ),
                ),
            },
        }),
        [
            api,
            selectedNodes,
            selectedQsGroups,
            selectedRoles,
            selectedUser,
            tenantId,
        ],
    );

    const {
        data: userApiData,
        loading: userApiLoading,
        error: userApiError,
        execute: saveUserData,
    } = useApi<User>(userApiConfig);

    const handleIHNodesLoading = (loading: boolean) => {
        setIsIHNodesLoading(loading);
    };

    useEffect(() => {
        if (userWasRemoved) return;
        if (users && users.length > 0) {
            let userIdDecoded = userId;

            try {
                userIdDecoded = decodeURIComponent(userId);
            } catch {
                // the decode crashes if there's no special character
                // so do nothing and just use the original userId from the param
            }

            const user = users.find(user => user.userId === userIdDecoded);
            if (user) {
                setSelectedUser(user);
            } else {
                // display error if the user could not be found among the list of users
                setShowDataError(true);
            }
        }
    }, [userId, users, transitionPostUserKill, userWasRemoved]);

    // load the initial data
    const initialQsGroups = React.useMemo(
        () => selectedUser?.qsGroups?.map(group => group.name) || [],
        [selectedUser],
    );

    useEffect(() => {
        setSelectedQsGroups(initialQsGroups);
    }, [initialQsGroups]);

    const initialRoles = React.useMemo(
        () => selectedUser?.roles?.map(role => role.name) || [],
        [selectedUser],
    );

    useEffect(() => {
        setSelectedRoles(initialRoles);
    }, [initialRoles]);

    const initialNodes = React.useMemo(
        () => selectedUser?.dataNodes || {},
        [selectedUser],
    );
    useEffect(() => {
        setSelectedNodes(initialNodes);
    }, [initialNodes]);

    // Retrieve available learn tenants and their root-level nodes.
    // Calls GET /v2/.../nodes/root WITHOUT learnTenantId.
    // Response keys are learn tenant IDs; values are their root-level nodes.
    useEffect(() => {
        if (!tenantId || !api?.userManagement || !accessToken) return;

        const parentRootNode = 'root';
        const fetchUrl = encodeURI(
            `${api.userManagement}/v2/tenants/${tenantId}/nodes/${parentRootNode}`,
        );

        const fetchLearnTenantNodes = async () => {
            setLearnTenantNodesLoading(true);
            setLearnTenantNodesError(undefined);
            try {
                const response = await axios.get<INodeResponse>(fetchUrl, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                // Transform API response (minimal {id, name}) to TreeNode format immediately
                const transformedNodes: Record<string, TreeNode[]> = {};
                Object.entries(response.data.nodes || {}).forEach(
                    ([tenantId, nodes]) => {
                        transformedNodes[tenantId] = nodes.map(n => ({
                            id: n.id,
                            name: n.name,
                            level: 0,
                            ancestorIds: [],
                            childrenIds: [],
                            sortableName: n.name,
                        }));
                    },
                );
                setLearnTenantNodes(transformedNodes);
            } catch {
                setLearnTenantNodesError(
                    'Could not load available learn tenants',
                );
            } finally {
                setLearnTenantNodesLoading(false);
            }
        };
        fetchLearnTenantNodes();
    }, [tenantId, api, accessToken]);

    useEffect(() => {
        if (usersApiError) {
            setShowDataError(true);
        }
    }, [usersApiError]);

    // handling of the user edit api request
    useEffect(() => {
        if (
            saveClicked &&
            !userApiLoading &&
            (userApiData !== undefined || userApiError !== null) &&
            replaceUserData
        ) {
            if (userApiError) {
                enqueueSnackbar(t('settings.editUser.snackbar.error'));
            } else {
                replaceUserData(userApiData as User);
                setSaveClicked(false);
                enqueueSnackbar(t('settings.editUser.snackbar.success'));
                history.push('/settings/');
            }
        }
    }, [
        enqueueSnackbar,
        history,
        replaceUserData,
        saveClicked,
        userApiData,
        userApiError,
        userApiLoading,
    ]);

    const handleSave = () => {
        if (
            userIlluminateRolesEnabled &&
            selectedRoles.length === 0 &&
            !emptyRolesDialogOpen
        ) {
            setEmptyRolesDialogOpen(true);
            return;
        }
        setSaveClicked(true);
        setEmptyRolesDialogOpen(false);
        // if removing author role from a user, we need to check assets of the user and transfer those if needed
        if (
            !selectedRoles.includes(AUTHOR_ROLE_NAME) &&
            initialRoles.includes(AUTHOR_ROLE_NAME)
        ) {
            setUserToAssetCheck(selectedUser);
        } else {
            saveUserData();
        }
    };

    const handleExitClick = (): undefined => {
        history.push(`/settings/`);
        return undefined;
    };

    const handleIHSelect = useCallback(
        (learnTenantId: string, newNodes: string[]) => {
            setSelectedNodes(prev => {
                const current = prev[learnTenantId] || [];
                if (arraysHaveSameElements(current, newNodes)) {
                    return prev; // no change, avoid re-render
                }
                return {
                    ...prev,
                    [learnTenantId]: newNodes,
                };
            });
        },
        [],
    );

    const handleRolesSelect = (newQsGroups: string[]) => {
        if (!arraysHaveSameElements(newQsGroups, selectedQsGroups)) {
            setSelectedQsGroups(newQsGroups);
        }
    };

    const hasUnsavedChanges = React.useMemo(
        () =>
            !arraysHaveSameElements(selectedQsGroups, initialQsGroups) ||
            !nodesRecordsAreEqual(selectedNodes, initialNodes) ||
            !arraysHaveSameElements(selectedRoles, initialRoles),
        [
            selectedQsGroups,
            initialQsGroups,
            selectedNodes,
            initialNodes,
            selectedRoles,
            initialRoles,
        ],
    );

    // Special role overrides:

    // Admins have all data and IHNodes access regardless of the selection: everything is checked and disabled
    const isAdmin = selectedRoles.includes(ADMIN_ROLE_NAME);
    const isDeveloper = selectedRoles.includes(DEVELOPER_ROLE_NAME);

    // Developer only (no other roles) - disable permissions, show nothing
    const isDeveloperOnly = isDeveloper && selectedRoles.length === 1;

    // Compute tooltip for disabled IH nodes: only shown when nodes are actually disabled
    const getDisabledTooltipForNodes = (): string | undefined => {
        if (isAdmin) {
            return t('settings.editUser.fixedAdminPermsTooltip');
        }
        if (isDeveloperOnly) {
            return t('settings.editUser.developerPermsTooltip');
        }
        return undefined;
    };

    // Compute display text for disabled QsGroups: shown when Developer role is selected
    const getDisabledDisplayTextForQsGroups = (): string | undefined => {
        if (isDeveloperOnly) {
            return t('settings.editUser.developerPermsDisplayText');
        }
        return undefined;
    };

    // Compute info text for disabled QsGroups: visible explanation rendered in the
    // permissions section when the selection is locked. Mirrors getDisabledTooltipForNodes()
    // so Admin and Developer-only each get role-appropriate wording instead of a
    // hard-coded admin message for both.
    const getDisabledInfoTextForQsGroups = (): string | undefined => {
        if (isAdmin) {
            return t('settings.editUser.fixedAdminPermsTooltip');
        }
        if (isDeveloperOnly) {
            return t('settings.editUser.developerPermsTooltip');
        }
        return undefined;
    };

    // Determine fixedQsGroups:
    // - Admin: ['BbDataReportViewer'] (all selected, disabled)
    // - Developer only: [] (nothing selected, disabled)
    // - Otherwise: undefined (editable)
    let fixedQsGroups: string[] | undefined;
    if (isAdmin) {
        fixedQsGroups = ['BbDataReportViewer'];
    } else if (isDeveloperOnly) {
        fixedQsGroups = [];
    }

    // New authors have all permissions set by default, but the selection can be changed
    const [newAuthorDefaultsAssigned, setNewAuthorDefaultsAssigned] =
        React.useState(false);
    React.useEffect(() => {
        const isNewAuthor =
            selectedRoles.includes(AUTHOR_ROLE_NAME) &&
            !initialRoles.includes(AUTHOR_ROLE_NAME);
        const developerOnly =
            selectedRoles.includes(DEVELOPER_ROLE_NAME) &&
            selectedRoles.length === 1;
        if (isAdmin) {
            // Admin always has BbDataReportViewer — keep selectedQsGroups in sync
            // so the PATCH request sends the correct value
            setSelectedQsGroups(['BbDataReportViewer']);
        } else if (developerOnly) {
            // Developer-only role has no pre-built report access (Snowflake only)
            setSelectedQsGroups([]);
        } else {
            setSelectedQsGroups(
                isNewAuthor ? ['BbDataReportViewer'] : initialQsGroups,
            );
        }
        setNewAuthorDefaultsAssigned(isNewAuthor);
    }, [isAdmin, selectedRoles, initialRoles, initialQsGroups]);

    const { seatsUsed, quotas } = useLicenseManagementContext();
    const isViewerSeatTaken = (roles: string[]) =>
        (roles.includes(VIEWER_ROLE_NAME) || roles.includes(ADMIN_ROLE_NAME)) &&
        !roles.includes(AUTHOR_ROLE_NAME);
    const willBreachViewerSeatLimit =
        !isViewerSeatTaken(initialRoles) &&
        isViewerSeatTaken(selectedRoles) &&
        seatsUsed &&
        quotas &&
        seatsUsed.reader >= quotas.reader;

    // ... breaching author seat limit should never happen because we disable author role checkbox proactively

    let nodeState:
        | 'all-ticked-and-disabled'
        | 'configurable'
        | 'all-unticked-and-disabled' = 'configurable';
    if (isAdmin) {
        nodeState = 'all-ticked-and-disabled';
    } else if (isDeveloperOnly) {
        nodeState = 'all-unticked-and-disabled';
    }

    const renderError = () => (
        <EditUserError error="An error occurred while loading the user data." />
    );

    const initialLoader = (
        <div className={classes.loadingIndicator}>
            <LoadingIndicator
                label={t('settings.editUser.loadingUser')}
                id="user-data-loading-indicator"
            />
        </div>
    );

    const savingDataLoader = (
        <div className={classes.loadingIndicator}>
            <LoadingIndicator
                label={t('settings.editUser.savingDataLoader')}
                id="saving-user-loading-indicator"
            />
        </div>
    );

    return (
        <Fade
            in={!transitionPostUserKill}
            timeout={300}
            onExited={() => {
                // transition out of this page after user deletion
                history.replace('/settings/');
            }}
            appear={false}
        >
            <div>
                <UnsavedChangesDialog
                    hasUnsavedChanges={
                        hasUnsavedChanges &&
                        !userToDelete &&
                        !saveClicked &&
                        !userWasRemoved &&
                        !transitionPostUserKill
                    }
                    onSaveAndLeave={handleSave}
                />
                <EmptyRolesDialog
                    open={emptyRolesDialogOpen}
                    userName={selectedUser?.userName || ''}
                    onProceed={handleSave}
                    onReturn={() => setEmptyRolesDialogOpen(false)}
                />
                <UserDeleteDialog
                    userToDelete={userToDelete}
                    setUserToDelete={setUserToDelete}
                    onSuccessfulDeletion={() => setUserWasRemoved(true)}
                    onClose={() => {
                        if (userWasRemoved) {
                            setTransitionPostUserKill(true);
                        }
                    }}
                />

                <UserAssetsTransferDialog
                    userToCheck={userToAssetCheck}
                    setUserToCheck={setUserToAssetCheck}
                    onSuccess={saveUserData}
                />
                {showDataError && renderError()}
                {!showDataError && (
                    <>
                        {loading || !selectedUser ? (
                            initialLoader
                        ) : (
                            <>
                                <ResponsiveBox className={classes.mainBox}>
                                    <Breadcrumbs
                                        menuButtonLabel={t(
                                            'settings.editUser.breadCrumbs.breadCrumbMenuButtonAriaLabel',
                                        )}
                                        menuId="menu-id-1"
                                        navigationLabel={t(
                                            'settings.editUser.breadCrumbs.breadCrumbAriaLabel',
                                        )}
                                        isCurrentLinkActive={false}
                                        data-testid="edit-users-breadcrumbs"
                                    >
                                        <BreadcrumbsItem
                                            label=""
                                            isCurrentPage={false}
                                        >
                                            <a
                                                data-testid="edit-users-breadcrumbs-back-link"
                                                onClick={handleExitClick}
                                                className={
                                                    classes.breadcrumbLink
                                                }
                                                href="#"
                                            >
                                                {t(
                                                    'settings.editUser.breadCrumbs.userManagementBreadcrumb',
                                                )}
                                            </a>
                                        </BreadcrumbsItem>
                                        <BreadcrumbsItem
                                            label=""
                                            isCurrentPage={true}
                                        >
                                            <a href="#">
                                                {t('settings.editUser.title')}
                                            </a>
                                        </BreadcrumbsItem>
                                    </Breadcrumbs>
                                </ResponsiveBox>
                                <ResponsiveBox
                                    className={classes.mainBox}
                                    dataTestId="edit-user-page-main-box"
                                >
                                    <Frame className={classes.frameReset}>
                                        {userApiLoading || loading ? (
                                            savingDataLoader
                                        ) : (
                                            <>
                                                <UserInfoContainer
                                                    userName={
                                                        selectedUser.userName
                                                    }
                                                    roles={selectedUser.roles}
                                                />
                                                <Divider />
                                                {willBreachViewerSeatLimit && (
                                                    <Banner
                                                        severity="warning"
                                                        icon={<Warning />}
                                                        data-testid="viewer-quota-warning-banner"
                                                        className={
                                                            classes.banner
                                                        }
                                                    >
                                                        <p role="alert">
                                                            {/* prettier-ignore */}
                                                            <Trans i18nKey="settings.illuminateRoles.viewerSeatsExceeded">
                                                                <b>Seats filled. </b> Hint.
                                                            </Trans>
                                                        </p>
                                                    </Banner>
                                                )}
                                                {userIlluminateRolesEnabled && (
                                                    <UserIlluminateRoles
                                                        userId={userId}
                                                        selectedRoles={
                                                            selectedRoles
                                                        }
                                                        initialRoles={
                                                            initialRoles
                                                        }
                                                        onRolesChange={
                                                            setSelectedRoles
                                                        }
                                                    />
                                                )}
                                                {/* TODO: integrate save, loading, error functionality in UserRoles component */}
                                                <UserRoles
                                                    userId={selectedUser.userId}
                                                    initialQsGroups={
                                                        selectedQsGroups
                                                    }
                                                    fixedQsGroups={
                                                        fixedQsGroups
                                                    }
                                                    disabledDisplayText={getDisabledDisplayTextForQsGroups()}
                                                    disabledInfoText={getDisabledInfoTextForQsGroups()}
                                                    onRolesChange={
                                                        handleRolesSelect
                                                    }
                                                />
                                                {learnTenantNodesError && (
                                                    <EditUserError
                                                        error={
                                                            learnTenantNodesError
                                                        }
                                                    />
                                                )}
                                                {learnTenantNodesLoading && (
                                                    <SkeletonTable />
                                                )}
                                                {Object.entries(
                                                    learnTenantNodes,
                                                )
                                                    .sort(([a], [b]) =>
                                                        a.localeCompare(b),
                                                    )
                                                    .map(([lt, rootNodes]) => (
                                                        <IHNodes
                                                            key={lt}
                                                            userId={
                                                                selectedUser.userId
                                                            }
                                                            learnTenantId={lt}
                                                            parentNodes={
                                                                rootNodes
                                                            }
                                                            nodeState={
                                                                nodeState
                                                            }
                                                            disabledTooltip={getDisabledTooltipForNodes()}
                                                            selectAllNodes={
                                                                newAuthorDefaultsAssigned
                                                            }
                                                            initialNodeIds={
                                                                initialNodes[
                                                                    lt
                                                                ] ??
                                                                emptyNodeIds
                                                            }
                                                            onLoading={
                                                                handleIHNodesLoading
                                                            }
                                                            onSelect={
                                                                handleIHSelect
                                                            }
                                                        />
                                                    ))}
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    className={
                                                        classes.actionBar
                                                    }
                                                >
                                                    <PrimaryButton
                                                        className={
                                                            classes.saveButton
                                                        }
                                                        onClick={handleSave}
                                                        disabled={
                                                            isIHNodesLoading ||
                                                            !hasUnsavedChanges ||
                                                            willBreachViewerSeatLimit
                                                        }
                                                        data-testid="edit-user-save-button"
                                                    >
                                                        {t(
                                                            'settings.editUser.saveButtonText',
                                                        )}
                                                    </PrimaryButton>
                                                    <DefaultButton
                                                        className={
                                                            classes.cancelButton
                                                        }
                                                        onClick={
                                                            handleExitClick
                                                        }
                                                        data-testid="edit-user-cancel-button"
                                                    >
                                                        {t(
                                                            'settings.editUser.cancelButtonText',
                                                        )}
                                                    </DefaultButton>
                                                    {!userIsDeletable ? (
                                                        <Tooltip
                                                            title={
                                                                userUndeletabilityReason
                                                            }
                                                            placement="top"
                                                            role="tooltip"
                                                            arrow
                                                        >
                                                            <div>
                                                                <DefaultButton
                                                                    className={
                                                                        classes.deleteButton
                                                                    }
                                                                    disabled={
                                                                        true
                                                                    }
                                                                    data-testid="edit-user-delete-button"
                                                                >
                                                                    {t(
                                                                        'settings.editUser.deleteButtonText',
                                                                    )}
                                                                </DefaultButton>
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <DefaultButton
                                                            className={
                                                                classes.deleteButton
                                                            }
                                                            onClick={() =>
                                                                setUserToDelete(
                                                                    selectedUser,
                                                                )
                                                            }
                                                            data-testid="edit-user-delete-button"
                                                        >
                                                            {t(
                                                                'settings.editUser.deleteButtonText',
                                                            )}
                                                        </DefaultButton>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </Frame>
                                    <ActionAreaButtons
                                        helpLink={getHelpLink('roles')}
                                    />
                                </ResponsiveBox>
                            </>
                        )}
                    </>
                )}
            </div>
        </Fade>
    );
};
